import { useCallback, useMemo, useRef, type ComponentType } from 'react'
import { atom, useAtom, useAtomValue, useSetAtom, useStore } from 'jotai'
import { atomFamily, atomWithStorage, selectAtom } from 'jotai/utils'
import { atomWithGlobalStore } from '@highlight/ipc/jotai'
// The leaf atoms module, not view-atoms: importing the latter would chain this
// module's atoms to the view registry's hot updates (see view-state-atoms.ts).
import {
  createSidepanelViewHistory,
  useViewScope,
  viewHistoryAtom,
  type FloatingBarCopy,
  type FloatingBarScrollSlot,
} from './view-state-atoms'
import { releaseFooterSlotClaim } from './footer-slot-claim'
import { useClaimAtom, type Claim } from './hooks/use-claim-atom'
import {
  DEFAULT_SIDEPANEL_SECTION_ORDER,
  isSidepanelSectionKey,
  type SidepanelSectionKey,
} from './sidepanel-section-order'
import {
  DEFAULT_SIDEPANEL_SIDE,
  SIDEPANEL_SIDE_SETTING_KEY,
  resolveSidepanelSide,
  type SidepanelSide,
} from '@shared/sidepanel/side'
import { SIDEPANEL_SURFACE_GUTTER_PX } from '@shared/sidepanel/background-blur'
import {
  applySidepanelGeometry,
  resolveSidepanelGeometry,
  SIDEPANEL_GEOMETRY_SETTING_KEY,
  type ResolvedSidepanelGeometry,
  type SidepanelDisplayGeometry,
  type SidepanelGeometryStore,
} from '@shared/sidepanel/display-geometry'
import { useOverlayDisplayKey } from '@renderer/overlay/hooks/use-overlay-display-key'
import { overlayDisplayKey } from '@shared/overlay/display'

/**
 * Sidepanel locked state - prevents backdrop click-to-close
 */
const sidepanelLockedAtom = atomWithGlobalStore<boolean>('general', 'sidepanel.locked', false)

export const useSidepanelLocked = () => {
  return useAtomValue(sidepanelLockedAtom)
}

export const useSetSidepanelLocked = () => {
  return useSetAtom(sidepanelLockedAtom)
}

/**
 * Track if sidepanel was opened via hotkey (disables auto-hide)
 */
const sidepanelHotkeyOpenedAtom = atom<boolean>(false)

export const useSidepanelHotkeyOpened = () => {
  return useAtomValue(sidepanelHotkeyOpenedAtom)
}

export const useSetSidepanelHotkeyOpened = () => {
  return useSetAtom(sidepanelHotkeyOpenedAtom)
}

export const useSidepanelHotkeyOpenedAtom = () => {
  return useAtom(sidepanelHotkeyOpenedAtom)
}

/**
 * Track if sidepanel was opened via actual keyboard hotkey (for tooltip suppression)
 */
const sidepanelOpenedViaKeybindAtom = atom<boolean>(false)

export const useSidepanelOpenedViaKeybind = () => {
  return useAtomValue(sidepanelOpenedViaKeybindAtom)
}

export const useSetSidepanelOpenedViaKeybind = () => {
  return useSetAtom(sidepanelOpenedViaKeybindAtom)
}

/**
 * Track sidepanel header height for scroll padding
 */
const sidepanelHeaderHeightAtom = atom<number>(0)

export const useSidepanelHeaderHeight = () => {
  return useAtomValue(sidepanelHeaderHeightAtom)
}

export const useSetSidepanelHeaderHeight = () => {
  return useSetAtom(sidepanelHeaderHeightAtom)
}

const sidepanelFindInPageOpenAtom = atom<boolean>(false)

export const useSidepanelFindInPageOpen = () => {
  return useAtomValue(sidepanelFindInPageOpenAtom)
}

export const useSetSidepanelFindInPageOpen = () => {
  return useSetAtom(sidepanelFindInPageOpenAtom)
}

// Height of the region between the header and the footer (Home body + open
// View in the overlay, the view router in a popout). It is the vertical room
// anything floating above the footer can take.
const sidepanelViewAreaHeightAtom = atom<number>(0)

export const useSidepanelViewAreaHeight = () => {
  return useAtomValue(sidepanelViewAreaHeightAtom)
}

export const useSetSidepanelViewAreaHeight = () => {
  return useSetAtom(sidepanelViewAreaHeightAtom)
}

// The atoms behind these hooks belong to the view scope (`view-state-atoms`):
// a View hosted outside the panel brings its own floating bar, sheet layer and
// glow, so each one resolves against whichever scope it renders in.

export type { FloatingBarScrollSlot }

export const useSidepanelFloatingBarHeight = () => {
  return useAtomValue(useViewScope().floatingBarHeight)
}

export const useSetSidepanelFloatingBarHeight = () => {
  return useSetAtom(useViewScope().floatingBarHeight)
}

export const useSidepanelBottomGlowTint = (): string | null => {
  return useAtomValue(useViewScope().bottomGlow)?.value ?? null
}

export const useClaimSidepanelBottomGlow = (tint: string | null | undefined | false): void => {
  useClaimAtom(useViewScope().bottomGlow, tint)
}

/** The copy control the floating bar should render, or null while nothing offers one. */
export const useFloatingBarCopy = (): FloatingBarCopy | null => {
  return useAtomValue(useViewScope().floatingBarCopy)?.value ?? null
}

/**
 * Offer the floating bar a copy control while this component owns what's on
 * screen — the panel for the open tab. Pass null when there is nothing to copy,
 * so the bar drops the button rather than offering an empty clipboard.
 */
export const useClaimFloatingBarCopy = (copy: FloatingBarCopy | null | undefined | false): void => {
  useClaimAtom(useViewScope().floatingBarCopy, copy)
}

export const useViewSheetLayer = () => {
  return useAtomValue(useViewScope().sheetLayer)
}

export const useSetViewSheetLayer = () => {
  return useSetAtom(useViewScope().sheetLayer)
}

export const useIsViewSheetOpen = (): boolean => {
  return useAtomValue(useViewScope().openSheet) !== null
}

export const useClaimViewSheetOpen = (open: boolean): void => {
  useClaimAtom(useViewScope().openSheet, open || null)
}

export const useFloatingBarScrollSlot = () => {
  return useAtomValue(useViewScope().floatingBarScrollSlot)
}

export const useSetFloatingBarScrollSlot = () => {
  return useSetAtom(useViewScope().floatingBarScrollSlot)
}

/** Held while a View is open in a popover beside the panel (`ViewPopover`). */
const hostedViewOpenAtom = atom<Claim<true> | null>(null)

export const useIsHostedViewOpen = (): boolean => {
  return useAtomValue(hostedViewOpenAtom) !== null
}

export const useClaimHostedViewOpen = (open: boolean): void => {
  useClaimAtom(hostedViewOpenAtom, open || null)
}

/**
 * Something has claimed the footer's bottom slot — the chat input's place —
 * and how tall it needs the slot to be. Null leaves the chat input in it.
 *
 * The claim carries what fills the slot as well as its height, so the footer
 * never learns which feature is asking and a surface isn't tied to the view it
 * was opened from: the meeting notepad is claimed from Home, the Meeting View
 * and the Action View alike, and stays put as the user moves between them.
 * A component, not an element, so the claim is a stable value across renders.
 */
export interface FooterSlotClaim {
  /**
   * Identity of the holder, so a release can tell its own claim from the next
   * one. Stamped by {@link useFooterSlotClaimant}, which is the only way in.
   */
  owner: object
  height: number
  component: ComponentType
  /**
   * This claim is sized to fill the panel rather than to stand in for the chat
   * input (the Notepad at full height), so the footer's box stops being a
   * layout input: the surfaces that pad themselves clear of it are behind the
   * claim, and padding them by most of the panel would collapse them — and cost
   * them their scroll position on the way back. See `SidepanelFooter`.
   */
  coversPanel?: boolean
  /**
   * Something is driving this height frame by frame (the Notepad's top edge
   * under a drag), so the slot takes it as given instead of morphing to it.
   */
  instant?: boolean
}

const footerSlotClaimAtom = atom<FooterSlotClaim | null>(null)

export const useFooterSlotClaim = () => {
  return useAtomValue(footerSlotClaimAtom)
}

/**
 * Stable imperative getter, the same trade as {@link useGetSidepanelScrollPosition}:
 * `SidepanelFooter` reads the claim inside its height-observer callback, where
 * subscribing would re-render the whole footer on every claim write — including
 * every frame of a Notepad resize drag, which is precisely the churn its settle
 * timer exists to keep off the panel. Reading through the store also means the
 * callback sees the current claim rather than one an effect had to mirror into
 * a ref after the observer had already fired.
 */
export const useGetFooterSlotClaim = () => {
  const store = useStore()
  return useCallback(() => store.get(footerSlotClaimAtom), [store])
}

/**
 * Take the footer's bottom slot for as long as this component is mounted.
 *
 * The owner token is minted here rather than passed in, which is what makes the
 * compare-and-clear in `releaseFooterSlotClaim` unforgettable: a holder can
 * only ever give back its own claim. That matters because holders overlap — the
 * view router animates a view out, so an outgoing host's release lands after
 * the incoming one has already taken the slot (HIG-6010).
 */
export const useFooterSlotClaimant = () => {
  const setClaim = useSetAtom(footerSlotClaimAtom)
  // One identity per component, not per claim: two hosts can hold the same
  // surface across a navigation, and the point of the token is to tell the
  // hosts apart.
  const owner = useRef({}).current

  return useMemo(
    () => ({
      claim: (claim: Omit<FooterSlotClaim, 'owner'>) => setClaim({ ...claim, owner }),
      release: () => setClaim(releaseFooterSlotClaim(owner)),
    }),
    [owner, setClaim],
  )
}

/**
 * Scroll position for sidepanel body (actions section)
 * In-memory only - no persistence across app restarts
 */
const sidepanelScrollPositionAtom = atom<number>(0)

/**
 * Stable imperative getter for the saved scroll position. Reading through the
 * store (instead of `useAtomValue`) means the scroll container can restore its
 * offset on mount without subscribing to the atom — so writing the position on
 * scroll-end never re-renders the body or its sections.
 */
export const useGetSidepanelScrollPosition = () => {
  const store = useStore()
  return useCallback(() => store.get(sidepanelScrollPositionAtom), [store])
}

export const useSetSidepanelScrollPosition = () => {
  return useSetAtom(sidepanelScrollPositionAtom)
}

/**
 * QuickTour state
 */
// Persisted because the panel can be closed mid-tour (step 1 teaches the summon
// hotkey) and only the post-onboarding hand-off ever starts the tour. In memory,
// a reload between closing and reopening would strand a user outside onboarding
// for good: never active, never completed, nothing left to re-arm it.
const quickTourActiveAtom = atomWithStorage<boolean>('highlight:quickTourActive', false)
const quickTourStepAtom = atomWithStorage<number>('highlight:quickTourStep', 0)
const quickTourCompletedAtom = atomWithStorage<boolean>('highlight:quickTourCompleted', false)

/**
 * Single entry point for starting the tour (post-onboarding hand-off and the
 * dev `quickTour:start` event both land here). Resetting view history is a
 * precondition, not a nicety: Home unmounts while a View is open (ADR 0001), so
 * the `data-tour-section` targets the tour spotlights wouldn't exist.
 */
export const useStartQuickTour = () => {
  const setViewHistory = useSetAtom(viewHistoryAtom)
  const setStep = useSetAtom(quickTourStepAtom)
  const setActive = useSetAtom(quickTourActiveAtom)

  return useCallback(() => {
    setViewHistory(createSidepanelViewHistory())
    setStep(0)
    setActive(true)
  }, [setViewHistory, setStep, setActive])
}

export const useQuickTourActive = () => useAtomValue(quickTourActiveAtom)
export const useQuickTourActiveAtom = () => useAtom(quickTourActiveAtom)
export const useQuickTourStep = () => useAtomValue(quickTourStepAtom)
export const useQuickTourStepAtom = () => useAtom(quickTourStepAtom)
export const useQuickTourCompleted = () => useAtomValue(quickTourCompletedAtom)
export const useSetQuickTourCompleted = () => useSetAtom(quickTourCompletedAtom)

/**
 * Per-action edit state - tracks in-flight edits and their results
 */
export interface ActionEditState {
  status: 'idle' | 'pending' | 'success' | 'error'
  message: string | null
  conversationId: string | null
}

export const stagedActionEditStatesAtom = atom<Record<string, ActionEditState>>({})

/**
 * This action's draft is being rewritten right now — a scoped-chat edit is in
 * flight and the copy on screen is the pre-edit one.
 *
 * Every surface offering to run the action reads this: executing a draft that's
 * mid-rewrite runs the version the user just asked to change. Subscribes to the
 * single action rather than the whole map so one action's edit doesn't re-render
 * every other card.
 */
export const isStagedActionEditPendingAtom = atomFamily((actionId: string) =>
  selectAtom(stagedActionEditStatesAtom, (states) => states[actionId]?.status === 'pending'),
)

export const useIsStagedActionEditPending = (actionId: string | undefined) =>
  useAtomValue(isStagedActionEditPendingAtom(actionId ?? ''))

/** Width the panel opens at before the user has ever resized it. */
export const DEFAULT_SIDEPANEL_WIDTH = 432
// Transparent gutter on the panel's inner edge (away from the screen edge).
// The unified panel surface needs only a slim margin: the resize handle
// straddles the surface edge and the close affordance lives in the bottom bar.
//
// Shared with the main process rather than declared here: the native blur
// subtracts this gutter to land its inner fade on the card's edge instead of
// the band's, so the two have to be the same number by construction.
export const DEFAULT_SIDEPANEL_LEFT_PADDING = SIDEPANEL_SURFACE_GUTTER_PX

export const clampSidepanelWidth = (width: number) =>
  Math.max(
    DEFAULT_SIDEPANEL_WIDTH,
    Math.min(width, Math.floor(window.innerWidth * 0.5) + DEFAULT_SIDEPANEL_LEFT_PADDING),
  )

/**
 * Sidepanel width, height and vertical position within the available band —
 * persisted per monitor (see `@shared/sidepanel/display-geometry`). `null` on
 * any field means "never chosen": the panel keeps tracking the band (see
 * `resolveSidepanelVerticalLayout`) as displays change, instead of freezing at
 * whatever the default happened to be on first run.
 *
 * Read *and* written from the mounted panel tree, which is the usage
 * `atomWithGlobalStore` supports — it only hydrates on mount, so a write-only
 * consumer mounted elsewhere would clobber the stored value with the default.
 */
const sidepanelGeometryAtom = atomWithGlobalStore<SidepanelGeometryStore>(
  'settings',
  SIDEPANEL_GEOMETRY_SETTING_KEY,
  {},
)

const useSidepanelGeometry = (): ResolvedSidepanelGeometry => {
  const store = useAtomValue(sidepanelGeometryAtom)
  const displayKey = useOverlayDisplayKey()
  return useMemo(() => resolveSidepanelGeometry(store, displayKey), [store, displayKey])
}

/**
 * Commits an explicit size/position choice for the monitor the overlay is on.
 * Fields commit together because a single gesture can change several (a corner
 * drag changes all three), and separate writes would render an intermediate
 * frame with the new height still at the old offset.
 *
 * `displayId` overrides the monitor to store against. A drag that ends on
 * another screen has to pass it: `overlay.displayId` is broadcast from main and
 * has not come back by the time pointerup commits, so the current key still
 * names the display the drag started on.
 */
export const useSetSidepanelGeometry = () => {
  const setGeometry = useSetAtom(sidepanelGeometryAtom)
  // Through a ref, not a dependency, so the callback identity stays stable and
  // the header's drag controls don't re-register on every display change.
  const displayKey = useOverlayDisplayKey()
  const displayKeyRef = useRef(displayKey)
  displayKeyRef.current = displayKey

  return useCallback(
    (patch: SidepanelDisplayGeometry, displayId?: number | null) => {
      const key = overlayDisplayKey(displayId) ?? displayKeyRef.current
      setGeometry((store) => applySidepanelGeometry(store, key, patch))
    },
    [setGeometry],
  )
}

export const useSidepanelCustomWidth = () => useSidepanelGeometry().width ?? DEFAULT_SIDEPANEL_WIDTH
export const useSidepanelCustomHeight = () => useSidepanelGeometry().height
export const useSidepanelVerticalOffset = () => useSidepanelGeometry().verticalOffset
export const useSidepanelRenderedWidth = () => clampSidepanelWidth(useSidepanelCustomWidth())

/**
 * True while a reposition drag is moving the panel. The drag writes its offset
 * to a motion value that bypasses React (see `useSidepanelDrag`), so anything
 * measuring against the panel has to re-measure for as long as this is true.
 */
const sidepanelRepositioningAtom = atom<boolean>(false)

export const useSidepanelRepositioning = () => useAtomValue(sidepanelRepositioningAtom)
export const useSetSidepanelRepositioning = () => useSetAtom(sidepanelRepositioningAtom)

/**
 * Width of the panel's visible surface: the rendered width minus the
 * transparent inner gutter (`--sidepanel-left-padding`). Use this when
 * positioning UI relative to the panel's visible content edge.
 */
export const sidepanelVisibleWidth = (renderedWidth: number) => renderedWidth - DEFAULT_SIDEPANEL_LEFT_PADDING

/**
 * Which screen edge the sidepanel is anchored to — persisted across sessions.
 * Everything that positions relative to the panel (peek trigger, resize handle,
 * views, blur, dock avoidance) derives its geometry from this.
 */
const sidepanelSideAtom = atomWithGlobalStore<SidepanelSide>(
  'settings',
  SIDEPANEL_SIDE_SETTING_KEY,
  DEFAULT_SIDEPANEL_SIDE,
)

// resolveSidepanelSide guards against a corrupt persisted value the same way
// the main-process consumers (Overlay blur, edge activation) already do.
export const useSidepanelSide = () => resolveSidepanelSide(useAtomValue(sidepanelSideAtom))
export const useSetSidepanelSide = () => useSetAtom(sidepanelSideAtom)

const sidepanelSectionOrderAtom = atomWithGlobalStore<Array<SidepanelSectionKey>>(
  'settings',
  'sidepanel.sectionOrder',
  [...DEFAULT_SIDEPANEL_SECTION_ORDER],
)

export const useSidepanelSectionOrder = () => useAtomValue(sidepanelSectionOrderAtom)
export const useSetSidepanelSectionOrder = () => useSetAtom(sidepanelSectionOrderAtom)

// Whether the To-dos Home's Upcoming keeps today's meetings once they've ended.
const sidepanelUpcomingShowsEndedAtom = atomWithGlobalStore<boolean>('settings', 'sidepanel.upcomingShowsEnded', false)

export const useSidepanelUpcomingShowsEnded = () => useAtom(sidepanelUpcomingShowsEndedAtom)

/**
 * Override placeholder for the chat input — set when opening a context-specific
 * chat (e.g. editing a skill). Cleared on new chat or after first submission.
 */
const chatInputPlaceholderAtom = atom<string | null>(null)
export const useChatInputPlaceholder = () => useAtomValue(chatInputPlaceholderAtom)
export const useSetChatInputPlaceholder = () => useSetAtom(chatInputPlaceholderAtom)

/**
 * Sections the user has collapsed from their header chevron — persisted, so a
 * panel the user has folded down stays folded across sessions and windows.
 * Stored as the collapsed set (not a per-key flag) so a section that doesn't
 * exist yet defaults to expanded.
 */
const sidepanelCollapsedSectionsAtom = atomWithGlobalStore<Array<SidepanelSectionKey>>(
  'settings',
  'sidepanel.collapsedSections',
  [],
)

const sanitizeCollapsedSections = (value: unknown): Array<SidepanelSectionKey> =>
  Array.isArray(value) ? value.filter(isSidepanelSectionKey) : []

export const useIsSidepanelSectionCollapsed = (key: SidepanelSectionKey) =>
  sanitizeCollapsedSections(useAtomValue(sidepanelCollapsedSectionsAtom)).includes(key)

export const useSetSidepanelSectionCollapsed = () => {
  const setCollapsed = useSetAtom(sidepanelCollapsedSectionsAtom)
  return useCallback(
    (key: SidepanelSectionKey, isCollapsed: boolean) =>
      setCollapsed((previous) => {
        const collapsed = sanitizeCollapsedSections(previous)
        if (collapsed.includes(key) === isCollapsed) return collapsed
        return isCollapsed ? [...collapsed, key] : collapsed.filter((k) => k !== key)
      }),
    [setCollapsed],
  )
}
